import React, { useCallback, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { ToastContainer } from 'react-toastify';
import { useInView } from 'react-intersection-observer';
import 'react-toastify/dist/ReactToastify.css';

import { useTheme } from '../contexts/ThemeProvider';
import Hero from '../sections/Hero';
import Nav from '../sections/Nav';
import About from '../sections/About';
import Experience from '../sections/Experience';
import Skills from '../sections/Skills';
import Projects from '../sections/Projects';
import Contact from '../sections/Contact';
import ChatWidget from '../components/ChatWidget';

const navItems = [
	{ id: 'home', label: 'Home' },
	{ id: 'about', label: 'About' },
	{ id: 'experience', label: 'Experience' },
	{ id: 'skill', label: 'Skills' },
	{ id: 'project', label: 'Projects' },
];

// A section's ref for scrolling, combined with its in-view tracking.
const useSection = (threshold) => {
	const ref = useRef(null);
	const { ref: inViewRef, inView } = useInView({ threshold });
	const setRef = useCallback(
		(node) => {
			ref.current = node;
			inViewRef(node);
		},
		[inViewRef]
	);
	return { ref, setRef, inView };
};

const Home = () => {
	const color = useTheme();

	const [activeSection, setActiveSection] = useState('home');
	const [showNav, setShowNav] = useState(false);
	const [prevScrollPos, setPrevScrollPos] = useState(window.scrollY);
	const [visible, setVisible] = useState(true);
	const [isBurger, setIsBurger] = useState(false);

	const sections = {
		home: useSection(0.5),
		about: useSection(0.1),
		experience: useSection(0.1),
		skill: useSection(0.1),
		project: useSection(0.1),
		contact: useSection(0.1),
	};
	const { home, about, experience, skill, project } = sections;

	const scrollToSection = (id) => {
		sections[id].ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
		setActiveSection(id);
		setIsBurger(false);
	};

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollPos = window.scrollY;
			const navbarVisible = prevScrollPos > currentScrollPos;
			const hero = home.ref.current;
			if (hero) {
				const heroBottom = hero.getBoundingClientRect().bottom;
				setShowNav(window.scrollY > heroBottom);
				if (currentScrollPos > heroBottom) {
					setVisible(navbarVisible);
					setPrevScrollPos(currentScrollPos);
					setIsBurger(false);
				}
			}
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prevScrollPos]);

	useEffect(() => {
		if (home.inView) {
			setActiveSection('home');
			setVisible(false);
		}
		if (about.inView) setActiveSection('about');
		if (experience.inView) setActiveSection('experience');
		if (skill.inView) setActiveSection('skill');
		if (project.inView) setActiveSection('project');
	}, [home.inView, about.inView, experience.inView, skill.inView, project.inView]);

	return (
		<Container color={color.color}>
			<ToastContainer />

			<Hero
				ref={home.setRef}
				showNav={showNav}
				navItems={navItems}
				activeSection={activeSection}
				onNavigate={scrollToSection}
			/>
			<Nav
				visible={visible}
				isBurger={isBurger}
				onBurgerChange={setIsBurger}
				navItems={navItems}
				activeSection={activeSection}
				onNavigate={scrollToSection}
			/>
			<About ref={about.setRef} />
			<Experience ref={experience.setRef} />
			<Skills ref={skill.setRef} />
			<Projects ref={project.setRef} />
			<Contact ref={sections.contact.setRef} />

			<ChatWidget />
		</Container>
	);
};

export default Home;

const Container = styled.div`
	background-color: ${({ color }) => color.background};
	width: 100vw;
	padding: 0 8rem;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	position: relative;
	a{
		text-decoration: none;
	}

	@media (max-width: 768px){
		.hamburger{
			opacity: 1;
		}
	}
`;
